import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Theme = 'light' | 'dark';
const LS_KEY = 'tv_theme';

interface ThemeState { theme: Theme; toggle: () => void; setTheme: (t: Theme) => void }
const Ctx = createContext<ThemeState | undefined>(undefined);

function detectInitial(): Theme {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem(LS_KEY) as Theme | null;
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(detectInitial);

  useEffect(() => { applyTheme(theme); localStorage.setItem(LS_KEY, theme); }, [theme]);

  return (
    <Ctx.Provider value={{
      theme,
      toggle: () => setThemeState(t => t === 'light' ? 'dark' : 'light'),
      setTheme: setThemeState,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useTheme() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme must be inside ThemeProvider');
  return v;
}
